import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { isEmpty } from 'validate.js';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import GoogleMapContainer from 'dpl/containers/GoogleMapContainer';

import Icon from './Icon';

const IS_LT_MD = isLessThanBreakpoint('md');

const MAP_MARKER_SIZE = IS_LT_MD ? '20px' : '24px';
const MAP_HEIGHT = IS_LT_MD ? '200px' : '226px';
const EXPANDED_MOBILE_MAP_HEIGHT = '455px';

const BASE_RADIUS_METERS = 8000;
const FIXED_RADIUS_PIXEL_SIZE = 30;
const MAX_ZOOM = 12;
const MIN_ZOOM = 3;

// MAP MARKER FUNCTIONS
/** @param {any} props */
function MapMarker({ isBreeder = false }) {
  if (isBreeder) {
    return (
      <div className="bg-blue-500 br-100 ph1 pv1 ph2-md pv2-md">
        <Icon
          className="white"
          name="fetch-house-heart"
          height={MAP_MARKER_SIZE}
          width={MAP_MARKER_SIZE}
        />
      </div>
    );
  }

  return (
    <div
      className="ba b--white bw2 br-100 bg-blue-500"
      style={{ width: '17px', height: '17px' }}
    />
  );
}

MapMarker.propTypes = {
  isBreeder: PropTypes.bool
};

function createMarker({ coordinates, map, isBreeder = false }) {
  if (isEmpty(coordinates)) return null;

  const markerComponent = document.createElement('div');
  createRoot(markerComponent).render(<MapMarker isBreeder={isBreeder} />);

  const marker = new window.google.maps.marker.AdvancedMarkerElement({
    anchorTop: '-50%',
    position: new window.google.maps.LatLng(coordinates),
    content: markerComponent,
    map
  });

  const circle = new window.google.maps.Circle({
    map,
    center: new window.google.maps.LatLng(coordinates),
    fillColor: '#0057DE',
    fillOpacity: 0.2,
    strokeOpacity: 0,
    radius: BASE_RADIUS_METERS
  });

  // Calculate meters per pixel at current zoom and latitude
  function getMetersPerPixel(lat) {
    const zoom = map.getZoom();
    // Formula: https://groups.google.com/g/google-maps-js-api-v3/c/hDRO4oHVSeM/m/osOYQYXg2oUJ
    const scale = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
    return scale;
  }

  // Update circle radius based on zoom level
  function updateCircleRadius() {
    const zoom = map.getZoom();
    const center = circle.getCenter();
    const lat = center.lat();

    if (zoom <= 8) {
      const metersPerPixel = getMetersPerPixel(lat);
      const fixedRadiusMeters = FIXED_RADIUS_PIXEL_SIZE * metersPerPixel;
      circle.setRadius(fixedRadiusMeters);
    } else {
      circle.setRadius(BASE_RADIUS_METERS);
    }
  }

  // Set initial radius
  updateCircleRadius();

  const zoomListener = map.addListener('zoom_changed', () => {
    const zoom = map.getZoom();
    if (isBreeder && zoom === MAX_ZOOM) {
      marker.map = null; // hide breeder marker at max zoom
    } else {
      marker.map = map; // show marker
    }

    updateCircleRadius();
  });

  return {
    marker,
    circle,
    cleanup: () => {
      window.google.maps.event.removeListener(zoomListener);
    }
  };
}

// CUSTOM RESIZE CONTROL BUTTON FUNCTIONS
function ResizeButton({ isExpanded, onClick }) {
  return (
    <button
      type="button"
      className="bg-white br3 secondary ph2 pv2"
      onClick={onClick}
      title="Click to resize map"
    >
      <Icon
        name={isExpanded ? 'fetch-close' : 'fetch-expand'}
        width="20px"
        height="20px"
      />
    </button>
  );
}

ResizeButton.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

function createResizeMapControl(map) {
  const controlContainer = document.createElement('div');
  controlContainer.style.margin = '10px 10px 0 0';
  const root = createRoot(controlContainer);

  let isExpanded = false;

  function renderButton() {
    root.render(
      <ResizeButton
        isExpanded={isExpanded}
        onClick={() => {
          const mapContainer = document.getElementById('mapContainer');
          if (!mapContainer) return;

          if (!isExpanded) {
            mapContainer.style.height = EXPANDED_MOBILE_MAP_HEIGHT;
            isExpanded = true;
          } else {
            mapContainer.style.height = MAP_HEIGHT;
            isExpanded = false;
          }
          renderButton(); // Re-render to update the icon
          window.google.maps.event.trigger(map, 'resize');
        }}
      />
    );
  }

  renderButton();

  return controlContainer;
}

export default function TransportationSectionMap({ coordinates }) {
  const [map, setMap] = useState(null);

  useEffect(() => {
    if (!map) return undefined;

    const allMarkers = coordinates.map((point, idx) =>
      createMarker({
        coordinates: { ...point },
        map,
        isBreeder: idx === 0
      })
    );

    if (IS_LT_MD) {
      const resizeControl = createResizeMapControl(map);

      map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(
        resizeControl
      );
    }

    return () => {
      allMarkers.forEach(({ marker, circle, cleanup }) => {
        if (cleanup) {
          cleanup();
        }
        marker?.setMap(null);
        circle?.setMap(null);
      });
    };
  }, [map, coordinates]);

  return (
    <div
      className="br3 overflow-hidden"
      id="mapContainer"
      style={{ height: MAP_HEIGHT, maxWidth: '424px' }}
    >
      <GoogleMapContainer
        className="TransportationSection__map"
        coordinates={coordinates}
        centerOnChange={coordinates.length > 1}
        boundsPadding={24}
        options={{
          gestureHandling: 'greedy',
          maxZoom: MAX_ZOOM,
          minZoom: MIN_ZOOM,
          zoom: 9
        }}
        onMapCreate={setMap}
        zoomable={!IS_LT_MD}
      />
    </div>
  );
}

TransportationSectionMap.propTypes = {
  coordinates: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired
    })
  ).isRequired
};
